/**
 * Board store: the one open board — its detail, lists, cards and field definitions, and
 * every board, list, card and field mutation on it, including card field values. Owns the
 * open board's detail (from boards.get) and pushes a summary update to the board tree
 * store whenever it changes the board. Loaded by the board page from the route and
 * replaced whole when the board changes. Reads swallow
 * failures into loadState; writes return an error message or null. Not persisted.
 */
import type {IBoard} from '@models/board';
import type {IBoardField} from '@models/board_field';
import type {IBoardList} from '@models/board_list';
import type {ICard} from '@models/card';
import {isTRPCClientError, trpcClient} from '@shared_ui/trpc_client';
import {buildTrpcErrorMessage} from '@shared_ui/trpc_errors';
import type {
   FieldConfig,
   FieldType,
   ICardFieldValues,
   IFieldDisplay,
   IFieldPerms,
} from '@shared/field_types';
import type {IRankPlacement} from '@shared/board_types';
import {compareRank, upsertRankedById} from '@src/store/board_tree_helpers';
import {type BoardPatch, useBoardTreeStore} from '@src/store/board_tree_store';
import {useOrgStore} from '@src/store/org_store';
import {selectedOptionIds} from '@c/fields/format/select_format';
import {defineStore} from 'pinia';

/** Assignee filter value meaning "cards with no assignee". */
export const UNASSIGNED_FILTER_ID = 'unassigned';

/** Card filter selections; an empty selection leaves that dimension inactive. */
export interface IBoardFilters {
   /** Selected assignee member ids; UNASSIGNED_FILTER_ID selects unassigned cards. */
   assigneeIds: string[];

   /** Selected select-option ids keyed by field id. */
   fieldOptionIds: Record<string, string[]>;
}

/** What the board page shows for the current board id. */
export enum BoardLoadState {
   /** No board selected. */
   IDLE = 'idle',
   LOADING = 'loading',
   LOADED = 'loaded',
   /** The server rejected the id: missing or another organization's. */
   MISSING = 'missing',
   /** The read failed for another reason; retryable. */
   FAILED = 'failed',
}

export interface IBoardState {
   /** The open board's detail; null until loaded or after it is cleared. */
   board: IBoard | null;

   /** The current state of the lists on this board. */
   lists: IBoardList[];

   /** The current state of the cards on this board. */
   cards: ICard[];

   /** The current state of the field definitions on this board. */
   fields: IBoardField[];

   /** Board the loaded detail, lists and cards belong to. */
   loadedBoardId: string | null;

   loadState: BoardLoadState;

   /** Incremented per load so a superseded load's response is discarded. */
   loadSeq: number;

   /** Title and description search; cleared when the board changes. */
   searchQuery: string;

   /** Card filter selections; cleared when the board changes. */
   filters: IBoardFilters;

   /** List whose add-card composer should open; the kanban column consumes and clears it. */
   cardComposerListId: string | null;
}

/** Card fields a save may change; an absent key leaves the stored value alone. */
export type CardPatch = Partial<
   Pick<ICard, 'title' | 'description' | 'assigneeMemberId' | 'hiddenFieldIds'>
>;

/**
 * Field definition fields a save may change; type is immutable and absent. Every block
 * is replaced whole, never merged.
 */
export type FieldPatch = Partial<Pick<IBoardField, 'name' | 'config' | 'display' | 'perms'>>;

const NO_BOARD_MESSAGE = 'No board is open';

/**
 * Whether the card passes every active filter dimension. Assignee matches by member id,
 * with UNASSIGNED_FILTER_ID matching a null assignee; a field filter matches when the
 * card's stored value shares an option id with the selection.
 */
function cardMatchesFilters(card: ICard, filters: IBoardFilters): boolean {
   if (
      filters.assigneeIds.length > 0 &&
      !filters.assigneeIds.includes(card.assigneeMemberId ?? UNASSIGNED_FILTER_ID)
   ) {
      return false;
   }
   for (const [field_id, option_ids] of Object.entries(filters.fieldOptionIds)) {
      const value_ids = selectedOptionIds(card.fieldValues[field_id] ?? null);
      if (!value_ids.some((value_id) => option_ids.includes(value_id))) {
         return false;
      }
   }
   return true;
}

/** Add the id to the list when absent, remove it when present. */
function toggleId(ids: string[], id: string): string[] {
   return ids.includes(id) ? ids.filter((entry) => entry !== id) : [...ids, id];
}

export const useBoardStore = defineStore('board', {
   state: (): IBoardState => ({
      board: null,
      lists: [],
      cards: [],
      fields: [],
      loadedBoardId: null,
      loadState: BoardLoadState.IDLE,
      loadSeq: 0,
      searchQuery: '',
      filters: {assigneeIds: [], fieldOptionIds: {}},
      cardComposerListId: null,
   }),

   getters: {
      orderedLists(state): IBoardList[] {
         return [...state.lists].sort(compareRank);
      },

      /** The board's field definitions in (rank, id) order. */
      orderedFields(state): IBoardField[] {
         return [...state.fields].sort(compareRank);
      },

      /**
       * The filter selections the loaded board can still answer: field filters whose field
       * no longer exists are dropped, so a stale URL never hides every card.
       */
      appliedFilters(state): IBoardFilters {
         const known_field_ids = new Set(state.fields.map((field) => field.id));
         const field_option_ids: Record<string, string[]> = {};
         for (const [field_id, option_ids] of Object.entries(state.filters.fieldOptionIds)) {
            if (known_field_ids.has(field_id) && option_ids.length > 0) {
               field_option_ids[field_id] = option_ids;
            }
         }
         return {assigneeIds: state.filters.assigneeIds, fieldOptionIds: field_option_ids};
      },

      /** How many filter values are applied, across all dimensions. */
      activeFilterCount(): number {
         const applied: IBoardFilters = this.appliedFilters;
         const option_count = Object.values(applied.fieldOptionIds).reduce(
            (sum, ids) => sum + ids.length,
            0,
         );
         return applied.assigneeIds.length + option_count;
      },

      /** Whether the search or any filter is narrowing the visible cards. */
      isNarrowed(state): boolean {
         return state.searchQuery.trim().length > 0 || this.activeFilterCount > 0;
      },

      /** Cards matching the search and the applied filters, in (rank, id) order. */
      visibleCards(state): ICard[] {
         const query = state.searchQuery.trim().toLowerCase();
         const applied: IBoardFilters = this.appliedFilters;
         const cards = state.cards.filter((card) => {
            const matches_search =
               query.length === 0 ||
               card.title.toLowerCase().includes(query) ||
               card.description.toLowerCase().includes(query);
            return matches_search && cardMatchesFilters(card, applied);
         });
         return cards.sort(compareRank);
      },

      /** The list's matching cards in (rank, id) order. */
      cardsForList(): (list_id: string) => ICard[] {
         return (list_id) => this.visibleCards.filter((card) => card.listId === list_id);
      },
   },

   actions: {
      // ---- [ Lookups ] ---- //

      /** Find a loaded card by id; null when the id is null or no card matches. */
      cardById(card_id: string | null | undefined): ICard | null {
         if (card_id == null) {
            return null;
         }
         return this.cards.find((card) => card.id === card_id) ?? null;
      },

      /** Find a loaded list by id; null when the id is null or no list matches. */
      listById(list_id: string | null | undefined): IBoardList | null {
         if (list_id == null) {
            return null;
         }
         return this.lists.find((list) => list.id === list_id) ?? null;
      },

      /** Find a loaded field definition by id; null when the id is null or no field matches. */
      fieldById(field_id: string | null | undefined): IBoardField | null {
         if (field_id == null) {
            return null;
         }
         return this.fields.find((field) => field.id === field_id) ?? null;
      },

      // ---- [ Loading ] ---- //

      /**
       * Open the board with this id: make it the current board and load its detail, lists
       * and cards, replacing whatever was open. A null id clears the store; an unchanged,
       * already-loaded id is a no-op.
       */
      async openBoard(board_id: string | null): Promise<void> {
         if (board_id == null) {
            this.clear();
            return;
         }
         if (board_id === this.loadedBoardId && this.loadState === BoardLoadState.LOADED) {
            return;
         }

         // The sequence survives the reset so a superseded load's response is still discarded.
         const next_seq = this.loadSeq + 1;
         this.$reset();
         this.loadSeq = next_seq;
         this.loadedBoardId = board_id;
         this.loadState = BoardLoadState.LOADING;
         await this.reload();
      },

      /**
       * Re-read the open board; sets loadState to MISSING when the server rejects the id
       * and FAILED when the read fails or there is no active organization.
       */
      async reload(): Promise<void> {
         const board_id = this.loadedBoardId;
         if (board_id == null) {
            return;
         }
         const organization_id = useOrgStore().activeOrg?.id ?? null;
         if (organization_id == null) {
            this.loadState = BoardLoadState.FAILED;
            return;
         }

         const seq = ++this.loadSeq;
         try {
            const client = trpcClient();
            const scope = {organizationId: organization_id, boardId: board_id};
            const [board, lists, cards, fields] = await Promise.all([
               client.boards.get.query(scope),
               client.boardLists.list.query(scope),
               client.cards.list.query(scope),
               client.boardFields.list.query(scope),
            ]);
            if (seq !== this.loadSeq) {
               return;
            }
            this.board = board;
            this.lists = lists;
            this.cards = cards;
            this.fields = fields;
            this.loadState = BoardLoadState.LOADED;
            useBoardTreeStore().applyBoardSummaryUpdate(board);
         } catch (err) {
            if (seq !== this.loadSeq) {
               return;
            }
            console.warn('Could not load the board', err);
            this.board = null;
            this.lists = [];
            this.cards = [];
            this.fields = [];
            this.loadState =
               isTRPCClientError(err) && err.data?.code === 'NOT_FOUND'
                  ? BoardLoadState.MISSING
                  : BoardLoadState.FAILED;
         }
      },

      /** Drop the open board and go back to IDLE. */
      clear(): void {
         this.$reset();
      },

      // ---- [ Board ] ---- //

      /**
       * Patch the open board's own fields (name, description, color) and return null or an
       * error message. Applies the result locally and updates the tree store's summary row.
       */
      async updateBoard(patch: BoardPatch): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const board = await trpcClient().boards.update.mutate({
               organizationId: scope.organizationId,
               boardId: scope.boardId,
               ...patch,
            });
            this.applyBoardDetail(board);
            useBoardTreeStore().applyBoardSummaryUpdate(board);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not save the board');
         }
      },

      // ---- [ Lists ] ---- //

      /** Create a list at the end of the board; returns the list or an error message. */
      async createList(name: string): Promise<{list: IBoardList} | {error: string}> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return {error: NO_BOARD_MESSAGE};
         }

         try {
            const list = await trpcClient().boardLists.create.mutate({
               organizationId: scope.organizationId,
               boardId: scope.boardId,
               name,
            });
            this.applyList(list);
            return {list};
         } catch (err) {
            return {error: buildTrpcErrorMessage(err, 'Could not create the list')};
         }
      },

      async renameList(list_id: string, name: string): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const list = await trpcClient().boardLists.update.mutate({
               organizationId: scope.organizationId,
               listId: list_id,
               name,
            });
            this.applyList(list);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not rename the list');
         }
      },

      /** Move a list to the placement among the board's lists; null placement appends. */
      async moveList(list_id: string, placement: IRankPlacement | null): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const list = await trpcClient().boardLists.move.mutate({
               organizationId: scope.organizationId,
               listId: list_id,
               placement: placement ?? undefined,
            });
            this.applyList(list);
            return null;
         } catch (err) {
            await this.reload();
            return buildTrpcErrorMessage(err, 'Could not move the list');
         }
      },

      /** Delete a list and its cards. */
      async deleteList(list_id: string): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const result = await trpcClient().boardLists.delete.mutate({
               organizationId: scope.organizationId,
               listId: list_id,
            });
            this.dropList(result.listId);
            for (const card_id of result.cardIds) {
               this.dropCard(card_id);
            }
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not delete the list');
         }
      },

      // ---- [ Cards ] ---- //

      /** Create a card at the end of the list; returns the card or an error message. */
      async createCard(list_id: string, title: string): Promise<{card: ICard} | {error: string}> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return {error: NO_BOARD_MESSAGE};
         }

         try {
            const card = await trpcClient().cards.create.mutate({
               organizationId: scope.organizationId,
               listId: list_id,
               title,
            });
            this.applyCard(card);
            return {card};
         } catch (err) {
            return {error: buildTrpcErrorMessage(err, 'Could not create the card')};
         }
      },

      async updateCard(card_id: string, patch: CardPatch): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const card = await trpcClient().cards.update.mutate({
               organizationId: scope.organizationId,
               cardId: card_id,
               ...patch,
            });
            this.applyCard(card);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not save the card');
         }
      },

      /**
       * Move a card into a list at the placement (null appends). Store state changes only
       * when the server answers; the drag surfaces keep the card where it was dropped until
       * then, and a rejected move reloads the board so it snaps back.
       */
      async moveCard(opts: {
         cardId: string;
         listId: string;
         placement: IRankPlacement | null;
      }): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const moved = await trpcClient().cards.move.mutate({
               organizationId: scope.organizationId,
               cardId: opts.cardId,
               listId: opts.listId,
               placement: opts.placement ?? undefined,
            });
            this.applyCard(moved);
            return null;
         } catch (err) {
            await this.reload();
            return buildTrpcErrorMessage(err, 'Could not move the card');
         }
      },

      async deleteCard(card_id: string): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const result = await trpcClient().cards.delete.mutate({
               organizationId: scope.organizationId,
               cardId: card_id,
            });
            this.dropCard(result.cardId);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not delete the card');
         }
      },

      // ---- [ Fields ] ---- //

      /**
       * Create a field on the open board at the placement (null appends); returns the
       * field or an error message.
       */
      async createField(opts: {
         type: FieldType;
         name: string;
         config?: FieldConfig;
         display?: Partial<IFieldDisplay>;
         placement?: IRankPlacement | null;
      }): Promise<{field: IBoardField} | {error: string}> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return {error: NO_BOARD_MESSAGE};
         }

         try {
            const field = await trpcClient().boardFields.create.mutate({
               organizationId: scope.organizationId,
               boardId: scope.boardId,
               type: opts.type,
               name: opts.name,
               config: opts.config,
               display: opts.display,
               placement: opts.placement ?? undefined,
            });
            this.applyField(field);
            return {field};
         } catch (err) {
            return {error: buildTrpcErrorMessage(err, 'Could not create the field')};
         }
      },

      /** Patch a field definition; an absent key leaves the stored value alone. */
      async updateField(field_id: string, patch: FieldPatch): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const field = await trpcClient().boardFields.update.mutate({
               organizationId: scope.organizationId,
               fieldId: field_id,
               ...patch,
            });
            this.applyField(field);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not save the field');
         }
      },

      /** Move a field to the placement among the board's fields; null placement appends. */
      async moveField(field_id: string, placement: IRankPlacement | null): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const field = await trpcClient().boardFields.move.mutate({
               organizationId: scope.organizationId,
               fieldId: field_id,
               placement: placement ?? undefined,
            });
            this.applyField(field);
            return null;
         } catch (err) {
            await this.reload();
            return buildTrpcErrorMessage(err, 'Could not move the field');
         }
      },

      /** Delete a field definition. Card values keyed by the deleted id stay stored. */
      async deleteField(field_id: string): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const result = await trpcClient().boardFields.delete.mutate({
               organizationId: scope.organizationId,
               fieldId: field_id,
            });
            this.dropField(result.fieldId);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not delete the field');
         }
      },

      /**
       * Write field values on a card; keys the patch does not name keep their stored value
       * and a null value clears one.
       */
      async setCardFieldValues(card_id: string, values: ICardFieldValues): Promise<string | null> {
         const scope = this.getBoardScope();
         if (scope == null) {
            return NO_BOARD_MESSAGE;
         }

         try {
            const card = await trpcClient().cards.setFieldValues.mutate({
               organizationId: scope.organizationId,
               cardId: card_id,
               values,
            });
            this.applyCard(card);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not save the field value');
         }
      },

      // ---- [ UI state ] ---- //

      setSearchQuery(query: string): void {
         this.searchQuery = query;
      },

      /** Replace every filter selection at once (URL seeding). */
      setFilters(filters: IBoardFilters): void {
         this.filters = filters;
      },

      /** Add or remove one assignee id from the assignee filter. */
      toggleAssigneeFilter(member_id: string): void {
         this.filters.assigneeIds = toggleId(this.filters.assigneeIds, member_id);
      },

      /** Add or remove one option id from a field's filter. */
      toggleFieldOptionFilter(field_id: string, option_id: string): void {
         const next = toggleId(this.filters.fieldOptionIds[field_id] ?? [], option_id);
         const {[field_id]: _removed, ...rest} = this.filters.fieldOptionIds;
         this.filters.fieldOptionIds = next.length === 0 ? rest : {...rest, [field_id]: next};
      },

      clearFilters(): void {
         this.filters = {assigneeIds: [], fieldOptionIds: {}};
      },

      /** Clear the search and every filter. */
      clearNarrowing(): void {
         this.clearFilters();
         this.searchQuery = '';
      },

      /** Ask the kanban column for this list to open its add-card composer. */
      requestCardComposer(list_id: string): void {
         this.cardComposerListId = list_id;
      },

      /** Mark the composer request handled. */
      clearCardComposerRequest(): void {
         this.cardComposerListId = null;
      },

      // ---- [ Row entry points ] ---- //

      /** Set the open board's detail; ignored when it is not the board this store holds. */
      applyBoardDetail(board: IBoard): void {
         if (board.id === this.loadedBoardId) {
            this.board = board;
         }
      },

      /** Insert or replace a list by id. */
      applyList(list: IBoardList): void {
         this.lists = upsertRankedById(this.lists, list);
      },

      dropList(list_id: string): void {
         this.lists = this.lists.filter((list) => list.id !== list_id);
      },

      /** Insert or replace a card by id. */
      applyCard(card: ICard): void {
         this.cards = upsertRankedById(this.cards, card);
      },

      dropCard(card_id: string): void {
         this.cards = this.cards.filter((card) => card.id !== card_id);
      },

      /** Insert or replace a field definition by id. */
      applyField(field: IBoardField): void {
         this.fields = upsertRankedById(this.fields, field);
      },

      dropField(field_id: string): void {
         this.fields = this.fields.filter((field) => field.id !== field_id);
      },

      /** The open board's scope (organization + board ids) for a mutation; null when no board or organization is active. */
      getBoardScope(): {organizationId: string; boardId: string} | null {
         const organization_id = useOrgStore().activeOrg?.id ?? null;
         const board_id = this.loadedBoardId;
         if (organization_id == null || board_id == null) {
            return null;
         }
         return {organizationId: organization_id, boardId: board_id};
      },
   },
});
